import React, { useRef, useState, useCallback, useEffect } from 'react';
import * as Styled from './MCarouselContainer.styles';

interface MCarouselContainerProps {
  children: React.ReactNode;
  slidesPerViewMobile?: number;
  slidesPerViewTablet?: number;
  slidesPerViewDesktop?: number;
  fixedSlideWidth?: number;
  gap?: number;
  className?: string;
  bleedLeft?: number;
}

export const MCarouselContainer: React.FC<MCarouselContainerProps> = ({
  children,
  slidesPerViewMobile = 1.5,
  slidesPerViewTablet = 2.5,
  slidesPerViewDesktop = 3.5,
  fixedSlideWidth,
  gap = 8,
  className,
  bleedLeft,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const items = React.Children.toArray(children).filter(Boolean);

  const checkScrollability = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const hasMoreContentRight = viewport.scrollWidth > viewport.clientWidth + viewport.scrollLeft + 1;
    const hasMoreContentLeft = viewport.scrollLeft > 1;
    setCanScrollRight(hasMoreContentRight);
    setCanScrollLeft(hasMoreContentLeft);
  }, []);

  useEffect(() => {
    checkScrollability();
    const viewport = viewportRef.current;
    if (viewport) {
      viewport.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
    }
    return () => {
      if (viewport) {
        viewport.removeEventListener('scroll', checkScrollability);
      }
      window.removeEventListener('resize', checkScrollability);
    };
  }, [checkScrollability, items.length]);

  const handleScrollLeft = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const slideWidth = fixedSlideWidth || viewport.clientWidth / slidesPerViewDesktop;
    viewport.scrollBy({ left: -(slideWidth + gap), behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const slideWidth = fixedSlideWidth || viewport.clientWidth / slidesPerViewDesktop;
    viewport.scrollBy({ left: slideWidth + gap, behavior: 'smooth' });
  };

  if (items.length === 0) return null;

  const isSingle = items.length === 1;

  return (
    <Styled.Container className={className}>
      <Styled.Viewport ref={viewportRef} $bleedLeft={bleedLeft}>
        <Styled.Rail $gap={gap}>
          {bleedLeft && bleedLeft > 0 && (
            <Styled.BleedSpacer aria-hidden="true" className="d-lg-none" $width={bleedLeft - gap} />
          )}
          {items.map((child, index) => (
            <Styled.Slide
              key={`carousel-slide-${index}`}
              $isSingle={isSingle}
              $gap={gap}
              $slidesPerViewMobile={slidesPerViewMobile}
              $slidesPerViewTablet={slidesPerViewTablet}
              $slidesPerViewDesktop={slidesPerViewDesktop}
              $fixedSlideWidth={fixedSlideWidth}
            >
              {child}
            </Styled.Slide>
          ))}
        </Styled.Rail>
      </Styled.Viewport>
      {canScrollLeft && (
        <Styled.ArrowButton type="button" $direction="left" onClick={handleScrollLeft} aria-label="Scroll left">
          <span className="icon-arrow-left" aria-hidden="true" />
        </Styled.ArrowButton>
      )}
      {canScrollRight && (
        <Styled.ArrowButton type="button" $direction="right" onClick={handleScrollRight} aria-label="Scroll right">
          <span className="icon-arrow-right" aria-hidden="true" />
        </Styled.ArrowButton>
      )}
    </Styled.Container>
  );
};
